import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import './MobileLocalGame.css';
import { AudioProvider } from '../poker-ui/AudioProvider';
import { GameIndustrialHeader } from '../poker-ui/GameIndustrialHeader';
import { PokerTableSurface } from '../poker-ui/PokerTableSurface';
import { IndustrialSeat } from '../poker-ui/IndustrialSeat';
import { SekaActionBar } from '../poker-ui/SekaActionBar';
import { PlayerStackBar } from '../poker-ui/PlayerStackBar';
import { PlayerHandStrip } from '../poker-ui/PlayerHandStrip';
import { PokerBetRaiseSlider } from '../poker-ui/PokerBetRaiseSlider';
import { PlayingCard } from '../poker-ui/PlayingCard';
import { parseSekaCard } from '../poker-ui/utils/parseSekaCard';
import { cn } from '../poker-ui/utils/cn';
import { MatchChronicleChain } from '../poker-ui/MatchChronicleChain';
import { LiveSekaHandMovesPanel, LiveSekaHandMovesTrail } from '../poker-ui/LiveSekaHandMovesPanel';
import {
  buildPlayerSnapshotsFromHistoryEntry,
  buildSimpleSekaOutcomeExportText,
  formatHistoryAction,
} from '../game/sekaHistoryUi';
import { FlickeringElement } from './GameEffects';
import { buildAllowedActionsForLocalCurrentTurn } from '../game/sekaAllowedActions';
import { LocalSekaGame } from '../game/sekaEngine';

function buildCommunitySlots(communityCards) {
  const slots = [null, null, null, null, null];
  (communityCards || []).forEach((c, i) => {
    if (i < 5) slots[i] = parseSekaCard(c);
  });
  return slots;
}

function dummyHoles(n = 3) {
  return Array.from({ length: n }, () => ({ suit: 'spades', rank: 'A' }));
}

/** Диапазон целевой верхней ставки (SEKA) для повышения; null если рейз невозможен. */
function sekaRaiseRange(game) {
  if (!game || game.gameEnded) return null;
  const p = game.players[game.currentPlayer];
  if (!p || p.isFolded) return null;
  const step = game.config?.minRaise ?? 10;
  const minRaise = game.currentBet + step;
  const maxRaise = Math.min(p.chips * (p.isDark ? 2 : 1), game.currentBet + p.chips);
  if (minRaise > maxRaise) return null;
  return { min: minRaise, max: maxRaise };
}

/**
 * Локальная «сека» 1×1 без привязки к балансу лобби; `onUserPatch` прокидывается из `App` для единообразия
 * с десктопной `LocalGame` (если позже появится экономика — тот же путь синка).
 */
function MobileLocalGameInner({ onBack, user = null, onUserPatch: _onUserPatch }) {
  const [game, setGame] = useState(new LocalSekaGame());
  const [gameStarted, setGameStarted] = useState(false);
  const [playerNames, setPlayerNames] = useState(['Игрок 1', 'Игрок 2']);
  const [gameStats, setGameStats] = useState({
    player1Wins: 0,
    player2Wins: 0,
    totalGames: 0
  });
  const [showRaiseInput, setShowRaiseInput] = useState(false);
  const [sekaRaiseAmount, setSekaRaiseAmount] = useState(0);
  /** Краткая хроника сессии (как «недавние события» в десктопной Секе). */
  const [matchChronicle, setMatchChronicle] = useState([]);
  const [outcomeCopyFeedback, setOutcomeCopyFeedback] = useState(false);
  const [outcomeDetailsExpanded, setOutcomeDetailsExpanded] = useState(false);
  const [liveMovesOpen, setLiveMovesOpen] = useState(false);
  const outcomeCopyTimerRef = useRef(null);

  const heroAvatarUrl = useMemo(() => {
    const u = user?.avatarUrl;
    return typeof u === 'string' && u.trim() ? u.trim() : '';
  }, [user?.avatarUrl]);

  useEffect(() => {
    if (!game.gameEnded) {
      setOutcomeDetailsExpanded(false);
      setLiveMovesOpen(false);
    }
  }, [game.gameEnded]);

  const raiseRange = useMemo(() => {
    if (!gameStarted || game.gameEnded || !showRaiseInput) return null;
    return sekaRaiseRange(game);
  }, [game, gameStarted, showRaiseInput]);

  const clampedSekaRaise = useMemo(() => {
    if (!raiseRange) return null;
    return Math.min(raiseRange.max, Math.max(raiseRange.min, sekaRaiseAmount));
  }, [raiseRange, sekaRaiseAmount]);

  // Начало новой игры
  const startNewGame = () => {
    const newGame = new LocalSekaGame();
    newGame.players[0].name = playerNames[0];
    newGame.players[1].name = playerNames[1];
    if (gameStarted && game?.gameEnded) {
      newGame.dealerIndex = (game.dealerIndex + 1) % newGame.players.length;
      newGame.players[0].chips = game.players[0].chips;
      newGame.players[1].chips = game.players[1].chips;
    }
    /* LocalSekaGame.dealCards(): анте, затем раздача карт (один атомарный вызов). */
    newGame.dealCards();
    setGame(newGame);
    setGameStarted(true);
    setShowRaiseInput(false);
    setOutcomeDetailsExpanded(false);
    setLiveMovesOpen(false);
  };

  // Ход игрока
  const handlePlayerMove = (action, amount = 0) => {
    const newGame = new LocalSekaGame();
    
    newGame.players = JSON.parse(JSON.stringify(game.players));
    newGame.currentPlayer = game.currentPlayer;
    newGame.pot = game.pot;
    newGame.currentBet = game.currentBet;
    newGame.roundNumber = game.roundNumber;
    newGame.gamePhase = game.gamePhase;
    newGame.gameEnded = game.gameEnded;
    newGame.winner = game.winner;
    newGame.winners = game.winners ? [...game.winners] : [];
    newGame.dealerIndex = game.dealerIndex;
    newGame.lastAggressorIndex = game.lastAggressorIndex;
    newGame.playersActedThisRound = new Set(game.playersActedThisRound || []);
    newGame.config = game.config;
    newGame.gameHistory = [...game.gameHistory];
    newGame.deck = [...game.deck];
    newGame.lastAnteContributions = Array.isArray(game.lastAnteContributions)
      ? game.lastAnteContributions.map((c) => ({ ...c }))
      : [];
    newGame.svarkaAnteMultiplier =
      game.svarkaAnteMultiplier != null ? Number(game.svarkaAnteMultiplier) : 1;
    newGame.handHadRaise = Boolean(game.handHadRaise);
    newGame.svarkaChainCount =
      game.svarkaChainCount != null ? Number(game.svarkaChainCount) : 0;
    newGame.currentHandActionLog = Array.isArray(game.currentHandActionLog)
      ? game.currentHandActionLog.map((a) => ({ ...a }))
      : [];
    newGame._moveSeq = game._moveSeq != null ? Number(game._moveSeq) : 0;
    newGame.currentHandStartStacks = Array.isArray(game.currentHandStartStacks)
      ? game.currentHandStartStacks.map((s) => ({ ...s }))
      : null;

    const upper = String(action || '').toUpperCase();
    const allowed = buildAllowedActionsForLocalCurrentTurn(newGame);
    if (!allowed.some((a) => String(a?.action || '').toUpperCase() === upper)) return;
    if (upper === 'RAISE' && !sekaRaiseRange(newGame)) return;

    const gameEnded = newGame.makeMove(game.currentPlayer, action, amount);
    
    if (gameEnded) {
      const winnerIndex = newGame.winner ? newGame.winner.id - 1 : -1;
      const tiedBoth =
        !newGame.winner && newGame.winners?.length === 2;
      setGameStats((prev) => ({
        ...prev,
        totalGames: prev.totalGames + 1,
        player1Wins:
          winnerIndex === 0 || tiedBoth ? prev.player1Wins + 1 : prev.player1Wins,
        player2Wins:
          winnerIndex === 1 || tiedBoth ? prev.player2Wins + 1 : prev.player2Wins,
      }));
      const gh = Array.isArray(newGame.gameHistory) ? newGame.gameHistory : [];
      const last = gh.length > 0 ? gh[gh.length - 1] : null;
      const subtitle = last?.pot != null ? `Банк ${Number(last.pot).toLocaleString('ru-RU')} SEKA` : undefined;
      setMatchChronicle((prev) =>
        [
          ...prev,
          {
            id: `m-${Date.now()}`,
            title: newGame.winner
              ? `Победил(а) ${newGame.winner.name}`
              : newGame.winners?.length > 1
                ? `Ничья: ${newGame.winners.map((w) => w.name).join(' · ')}`
                : 'Раздача завершена',
            subtitle,
          },
        ].slice(-12)
      );
    }

    setGame(newGame);
    setShowRaiseInput(false);
  };

  const getAvailableActions = () => {
    if (game.gameEnded || game.gamePhase === 'svarkaBetween') return [];
    const base = buildAllowedActionsForLocalCurrentTurn(game);
    const colorByAction = {
      FOLD: 'red',
      LOOK: 'gold',
      CALL: 'green',
      CHECK: 'blue',
      RAISE: 'orange',
      SHOWDOWN: 'purple',
    };
    return base
      .filter((a) => (String(a.action).toUpperCase() !== 'RAISE' ? true : Boolean(sekaRaiseRange(game))))
      .map((a) => ({
        ...a,
        color: colorByAction[String(a.action).toUpperCase()] || 'slate',
      }));
  };

  const onBarAction = (action) => {
    const allowed = getAvailableActions().map((a) => String(a.action).toUpperCase());
    if (!allowed.includes(String(action || '').toUpperCase())) return;
    if (action === 'RAISE') {
      const r = sekaRaiseRange(game);
      if (!r) return;
      setSekaRaiseAmount(r.min);
      setShowRaiseInput(true);
      return;
    }
    handlePlayerMove(action);
  };

  const lastHistoryEntry = useMemo(() => {
    const gh = Array.isArray(game.gameHistory) ? game.gameHistory : [];
    return gh.length > 0 ? gh[gh.length - 1] : null;
  }, [game.gameHistory]);

  const outcomePanelSnapshots = useMemo(
    () => (lastHistoryEntry ? buildPlayerSnapshotsFromHistoryEntry(lastHistoryEntry) : []),
    [lastHistoryEntry]
  );

  const outcomeModalHandActions = useMemo(() => {
    if (!game.gameEnded || !lastHistoryEntry) return [];
    const a = lastHistoryEntry.actions;
    return Array.isArray(a) ? a : [];
  }, [game.gameEnded, lastHistoryEntry]);

  const liveHandActionLog = useMemo(() => {
    if (game.gameEnded) return [];
    const log = game.currentHandActionLog;
    return Array.isArray(log) ? [...log] : [];
  }, [game]);

  const handleCopyOutcomePanel = useCallback(async () => {
    const text = buildSimpleSekaOutcomeExportText(matchChronicle, lastHistoryEntry);
    try {
      await navigator.clipboard.writeText(text);
      setOutcomeCopyFeedback(true);
      if (outcomeCopyTimerRef.current) clearTimeout(outcomeCopyTimerRef.current);
      outcomeCopyTimerRef.current = setTimeout(() => {
        setOutcomeCopyFeedback(false);
        outcomeCopyTimerRef.current = null;
      }, 2200);
    } catch {
      /* ignore */
    }
  }, [matchChronicle, lastHistoryEntry]);

  if (!gameStarted) {
    return (
      <div className="mobile-local-game">
        <div className="mobile-local-prestart-stack">
        <div className="mobile-game-header">
          <h1>📱 Мобильная игра "Сека"</h1>
          <p>Играйте с корешем за одним телефоном!</p>
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            🏠 Вернуться в меню
          </button>
        </div>

        <div className="mobile-game-setup">
          <h2>👥 Настройка игроков</h2>
          
          <div className="player-setup">
            <div className="player-input">
              <label>Игрок 1:</label>
              <input
                type="text"
                value={playerNames[0]}
                onChange={(e) => setPlayerNames([e.target.value, playerNames[1]])}
                placeholder="Введите имя игрока 1"
                maxLength={20}
              />
            </div>
            
            <div className="player-input">
              <label>Игрок 2:</label>
              <input
                type="text"
                value={playerNames[1]}
                onChange={(e) => setPlayerNames([playerNames[0], e.target.value])}
                placeholder="Введите имя игрока 2"
                maxLength={20}
              />
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary start-game-btn"
            onClick={startNewGame}
            disabled={!playerNames[0].trim() || !playerNames[1].trim()}
          >
            🚀 Начать игру
          </button>
        </div>
        </div>
      </div>
    );
  }

  const communitySlots = buildCommunitySlots([]);
  const dealerIndex = gameStats.totalGames % 2;
  const actingPlayer = !game.gameEnded ? game.players[game.currentPlayer] : null;
  /** Полоса: при наличии карт у героя — место 0; иначе максимальный seat с картами (номиналы чужих до вскрытия не показываем). */
  const stripSeat =
    !game.gameEnded
      ? (() => {
          const withHoles = [0, 1].filter((s) => game.players[s]?.cards?.length > 0);
          if (withHoles.includes(0)) return 0;
          return withHoles.length ? Math.max(...withHoles) : game.currentPlayer;
        })()
      : 0;
  const stripPlayer = !game.gameEnded ? game.players[stripSeat] : null;
  const stripParsed = stripPlayer
    ? (stripPlayer.cards || []).map(parseSekaCard).filter(Boolean)
    : [];
  const stripCards = stripParsed.length ? stripParsed : dummyHoles(3);

  const renderSeat = (player, seatPlayerIndex, opts = {}) => {
    const { compactTop = false } = opts;
    const parsed = (player.cards || []).map(parseSekaCard).filter(Boolean);
    const cardModels = parsed.length ? parsed : dummyHoles(3);
    const showHoleFaces =
      game.gameEnded || (seatPlayerIndex === 0 && !player.isDark);
    return (
      <div key={player.id} className={compactTop ? 'flex justify-center' : ''}>
        <IndustrialSeat
          name={player.name.length > 12 ? `${player.name.slice(0, 11)}…` : player.name}
          chips={player.chips}
          bet={player.currentBet || 0}
          cards={cardModels}
          compact
          isDealer={seatPlayerIndex === dealerIndex}
          isCurrentTurn={!game.gameEnded && game.currentPlayer === seatPlayerIndex}
          isFolded={Boolean(player.isFolded)}
          isWinner={
            game.gameEnded &&
            (game.winner?.id === player.id ||
              (game.winners || []).some((w) => w.id === player.id))
          }
          showCards={showHoleFaces}
          isUser={seatPlayerIndex === 0}
          isBot={seatPlayerIndex !== 0}
          seatColorIndex={seatPlayerIndex}
          avatarUrl={seatPlayerIndex === 0 ? heroAvatarUrl : ''}
        >
          {player.isDark && !game.gameEnded && (
            <FlickeringElement speed="normal">
              <div className="mt-1 rounded border border-white/10 bg-black/50 px-1.5 py-0.5 text-[10px] text-neon-gold">
                В темную
              </div>
            </FlickeringElement>
          )}
        </IndustrialSeat>
      </div>
    );
  };

  return (
    <div className="pt-root relative flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-cyan/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon-orange/40 to-transparent" />
        <div className="absolute top-0 bottom-0 left-0 w-px bg-gradient-to-b from-neon-cyan/20 via-transparent to-neon-orange/20" />
        <div className="absolute top-0 bottom-0 right-0 w-px bg-gradient-to-b from-neon-cyan/20 via-transparent to-neon-orange/20" />
      </div>

      <GameIndustrialHeader
        floating
        tableName={`${playerNames[0]} vs ${playerNames[1]}`}
        blindsLabel={`Раунд ${game.roundNumber} · Банк ${game.pot} · ${game.currentBet} SEKA`}
        playersCount={2}
        maxPlayers={6}
        onLeave={onBack}
      />

      <div className="px-3 py-2 text-center font-mono text-[10px] text-muted-foreground">
        {playerNames[0]}: <span className="text-neon-gold">{gameStats.player1Wins}</span>
        <span className="mx-2 text-border">|</span>
        {playerNames[1]}: <span className="text-neon-gold">{gameStats.player2Wins}</span>
        <span className="mx-2 text-border">|</span>
        Игр: {gameStats.totalGames}
      </div>

      <main className="relative z-10 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 pb-4">
        {!game.gameEnded ? (
          <>
            <div className="mx-auto w-full max-w-[min(100%,28rem)]">
              <div className="grid min-h-[118px] grid-cols-3 items-start gap-2 px-1">
                <div />
                {renderSeat(game.players[1 - game.currentPlayer], 1 - game.currentPlayer)}
                <div />
              </div>
            </div>

            <div className="relative flex min-h-[160px] flex-1 items-center justify-center py-1.5">
              <PokerTableSurface
                communitySlots={communitySlots}
                pot={game.pot}
                potLabel="POT"
                showCommunity={false}
              />
            </div>

            <div className="text-center text-xs text-muted-foreground">
              Ход:{' '}
              <span className="font-semibold text-neon-cyan">
                {game.players[game.currentPlayer].name}
              </span>
            </div>

            <PlayerStackBar stackAmount={actingPlayer.chips} />
            <PlayerHandStrip
              cards={stripCards}
              handName={
                stripPlayer && stripSeat === 0 && !stripPlayer.isDark
                  ? game.evaluateHand(stripPlayer.cards).description
                  : undefined
              }
              faceDown={!stripPlayer || stripSeat !== 0 || stripPlayer.isDark}
            />

            <div className="sticky bottom-0 z-30 bg-gradient-to-t from-background via-background to-transparent pb-2 pt-1.5">
              {showRaiseInput && raiseRange && clampedSekaRaise != null ? (
                <div className="w-full px-1">
                  <PokerBetRaiseSlider
                    min={raiseRange.min}
                    max={raiseRange.max}
                    value={clampedSekaRaise}
                    onChange={setSekaRaiseAmount}
                    className="pb-2"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setShowRaiseInput(false)}
                      className="flex h-12 items-center justify-center rounded-lg border border-border bg-card/80 text-xs font-semibold text-foreground hover:bg-muted/40"
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePlayerMove('RAISE', clampedSekaRaise)}
                      className="flex h-12 items-center justify-center rounded-lg border border-neon-orange/50 bg-neon-orange/15 text-xs font-semibold text-neon-orange hover:bg-neon-orange/25"
                    >
                      Поднять до {clampedSekaRaise.toLocaleString('ru-RU')}
                    </button>
                  </div>
                </div>
              ) : (
                <SekaActionBar
                  actions={getAvailableActions()}
                  onAction={onBarAction}
                  disabled={game.currentPlayer !== 0}
                />
              )}
              {!showRaiseInput ? (
                <div className="mt-2 w-full px-1">
                  <button
                    type="button"
                    onClick={() => setLiveMovesOpen((o) => !o)}
                    aria-expanded={liveMovesOpen}
                    className="inline-flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg border border-white/12 bg-black/20 px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-amber-100/95 transition hover:bg-black/30"
                  >
                    {liveMovesOpen ? (
                      <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    )}
                    Ходы раздачи
                    {liveHandActionLog.length > 0 ? (
                      <span className="rounded-full border border-amber-400/35 bg-amber-500/15 px-1.5 py-0 text-[9px] font-mono tabular-nums text-amber-50">
                        {liveHandActionLog.length}
                      </span>
                    ) : null}
                    {!liveMovesOpen && liveHandActionLog.length > 0 ? (
                      <LiveSekaHandMovesTrail actions={liveHandActionLog} className="min-w-0 flex-1 basis-full justify-center" />
                    ) : null}
                  </button>
                  {liveMovesOpen ? (
                    <div className="mt-1.5 max-h-[min(40vh,280px)] overflow-y-auto rounded-lg border border-amber-500/25 bg-black/30 px-2 py-2 text-left">
                      <LiveSekaHandMovesPanel actions={liveHandActionLog} />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/25">
              <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
                <div
                  className={cn(
                    'pointer-events-auto w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border px-4 py-4 text-center sm:px-6 sm:py-5',
                    'poker-fx-win-panel border border-emerald-400/50 bg-gradient-to-b from-emerald-950/95 to-[#0a1520]/95 shadow-[0_0_60px_rgba(52,211,153,0.25)]'
                  )}
                >
                {game.winner ? (
                  <>
                    <p className="text-xl font-black uppercase tracking-wide text-emerald-100">Победитель</p>
                    <p className="mt-1 text-lg font-bold text-white">{game.winner.name}</p>
                    <p className="mt-2 text-sm text-emerald-100/90">
                      {game.evaluateHand(game.winner.cards).description}
                    </p>
                  </>
                ) : game.winners && game.winners.length > 0 ? (
                  <>
                    <p className="text-xl font-black uppercase tracking-wide text-emerald-100">Ничья</p>
                    <p className="mt-2 text-sm text-white/90">{game.winners.map((w) => w.name).join(' · ')}</p>
                  </>
                ) : (
                  <p className="text-lg font-bold text-muted-foreground">Никто не выиграл</p>
                )}

                <div className="mt-4 flex w-full flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-2.5 py-1.5 text-[10px] font-bold text-amber-100 transition hover:bg-black/45"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Назад
                  </button>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleCopyOutcomePanel}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition',
                        outcomeCopyFeedback
                          ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'
                          : 'border-emerald-400/45 bg-emerald-500/12 text-emerald-100 hover:bg-emerald-500/22'
                      )}
                    >
                      <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {outcomeCopyFeedback ? 'Снимок скопирован' : 'Копировать снимок (хроника + раздача)'}
                    </button>
                  </div>
                </div>

                {(matchChronicle.length > 0 ||
                  outcomePanelSnapshots.length > 0 ||
                  outcomeModalHandActions.length > 0) && (
                  <button
                    type="button"
                    onClick={() => setOutcomeDetailsExpanded((v) => !v)}
                    aria-expanded={outcomeDetailsExpanded}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-black/25 px-3 py-2 text-[11px] font-bold text-white/90 transition hover:bg-black/35"
                  >
                    {outcomeDetailsExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                        Скрыть детали и историю
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                        Показать детали и историю
                      </>
                    )}
                  </button>
                )}

                {outcomeDetailsExpanded ? (
                  <>
                    {matchChronicle.length > 0 ? (
                      <div className="mt-3 max-h-[20vh] w-full overflow-y-auto rounded-lg border border-amber-500/25 bg-black/25 px-2 py-2 text-left">
                        <p className="text-center text-[10px] font-bold uppercase tracking-wide text-amber-200/90">
                          Недавние события
                        </p>
                        <div className="mt-1.5 space-y-1.5">
                          {matchChronicle.slice(-6).map((e) => (
                            <div
                              key={e.id}
                              className="rounded border border-white/10 bg-black/30 px-2 py-1 text-[9px] text-amber-100/90"
                            >
                              <span className="font-semibold text-amber-50">{e.title}</span>
                              {e.subtitle ? <span className="ml-1 text-amber-200/75">{e.subtitle}</span> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {outcomePanelSnapshots.length > 0 ? (
                      <div className="mt-3 w-full rounded-lg border border-cyan-500/30 bg-black/25 px-2 py-2 text-left">
                        <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-cyan-100/90">
                          Запись движка — оба места
                        </p>
                        <div className="mt-2 max-h-[28vh] space-y-2 overflow-y-auto">
                          {outcomePanelSnapshots.map((ps) => (
                            <div
                              key={ps.key}
                              className={cn(
                                'rounded-lg border px-2 py-1.5',
                                ps.isWinner ? 'border-emerald-500/45 bg-emerald-950/30' : 'border-white/10 bg-black/35'
                              )}
                            >
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                <span className="text-[10px] font-bold text-white/95">
                                  {ps.name}
                                  <span className="ml-1 font-mono text-[9px] font-normal text-white/45">
                                    м.{ps.seatIndex + 1}
                                  </span>
                                </span>
                                {ps.isWinner ? (
                                  <span className="rounded bg-emerald-500/25 px-1 py-0 text-[9px] font-bold uppercase text-emerald-100">
                                    победа
                                  </span>
                                ) : null}
                                {ps.isFolded ? (
                                  <span className="rounded bg-white/10 px-1 py-0 text-[9px] text-white/65">сброс</span>
                                ) : null}
                              </div>
                              {ps.handDescription ? (
                                <p className="mt-0.5 text-[9px] text-amber-200/90">{ps.handDescription}</p>
                              ) : null}
                              <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                                {ps.cardsUi.length > 0 ? (
                                  ps.cardsUi.map((c, ci) => (
                                    <PlayingCard key={`${ps.key}-c${ci}`} suit={c.suit} rank={c.rank} size="sm" />
                                  ))
                                ) : (
                                  <span className="text-[9px] text-white/45">нет карт</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-3 max-h-[min(32vh,260px)] w-full overflow-y-auto rounded-lg border border-emerald-500/30 bg-black/20 px-2 py-2 text-left">
                      <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-emerald-200/90">
                        История ходов
                      </p>
                      <p className="mt-0.5 text-center text-[9px] text-white/55">Порядок со стрелками</p>
                      {outcomeModalHandActions.length > 0 ? (
                        <>
                          <MatchChronicleChain
                            steps={outcomeModalHandActions.map(
                              (a, actionIdx) =>
                                `${actionIdx + 1}. ${a?.playerName ?? '—'}: ${formatHistoryAction(a)}`
                            )}
                          />
                          <ol className="mt-2 space-y-1 border-t border-white/10 pt-2">
                            {outcomeModalHandActions.map((a, actionIdx) => (
                              <li
                                key={`mob-act-${actionIdx}-${a?.playerId ?? 'x'}`}
                                className="flex items-center justify-between gap-2 rounded bg-black/30 px-2 py-1 text-[10px]"
                              >
                                <span className="truncate text-left text-white/90">
                                  {actionIdx + 1}. {a?.playerName ?? '—'}
                                </span>
                                <span className="shrink-0 text-emerald-100">{formatHistoryAction(a)}</span>
                              </li>
                            ))}
                          </ol>
                        </>
                      ) : (
                        <p className="mt-2 text-center text-[10px] text-white/65">
                          Действия не записаны (авто-завершение или до старта торговли).
                        </p>
                      )}
                    </div>
                  </>
                ) : null}

                <button
                  type="button"
                  onClick={startNewGame}
                  className="mt-6 w-full rounded-lg border border-neon-cyan/45 bg-neon-cyan/15 py-2.5 text-sm font-semibold text-neon-cyan transition hover:bg-neon-cyan/25"
                >
                  Новая игра
                </button>
                </div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-[min(100%,22rem)] opacity-40">
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {renderSeat(game.players[0], 0, { compactTop: true })}
                {renderSeat(game.players[1], 1, { compactTop: true })}
              </div>
            </div>

            <div className="relative flex min-h-[180px] flex-1 items-center justify-center py-1.5 opacity-40">
              <PokerTableSurface
                communitySlots={communitySlots}
                pot={game.pot}
                potLabel="POT"
                showCommunity={false}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default function MobileLocalGame(props) {
  return (
    <AudioProvider>
      <MobileLocalGameInner {...props} />
    </AudioProvider>
  );
}
